// A current sensor's energy-page settings — the tariff formula, billing cycle,
// working pattern, sanctioned load, and the real bills entered for the check.
//
// Stored as ONE JSON document on the sensor's own install row,
// DevicesInstalled.EnergySettings (Home Automation energy_settings_migration.sql,
// run once). Per DEVICE, not per location: a bill belongs to one meter, and a
// location can have two.
//
// ⚠️ Read from the SERVER row (`select a.*`), never the phone cache — the
// cached room rows name their columns and do not carry this one, and a tariff
// edited on the desktop must show on the phone straight away.
//
// Before the migration has run the column does not exist: a read finds no key
// and a write comes back as an error object (HTTP 200, like every failure on
// this backend). The page then keeps the settings in this browser only and
// SAYS so — never "Saved" for something only this phone knows.

import { getDeviceDetailFresh, getDeviceInstalledById, updateDeviceInstalled } from '../api/api';
import { DEFAULT_TARIFF, normalizeTariff } from './energyTariff';

export const DEFAULT_SETTINGS = Object.freeze({
  v: 1,
  tariff: DEFAULT_TARIFF,
  cycleDay: 1,
  offDays: [0],
  workStart: 9,
  workEnd: 21,
  sanctionedKw: 0,
  bills: [],
  // Appliances alone on a phase (e.g. 801038's office AC on phase 3), at most 3.
  appliances: [],
});

const LOCAL = 'energy.v1.settings.';

const int = (v, lo, hi, d) => {
  const x = parseInt(v, 10);
  return Number.isFinite(x) ? Math.min(hi, Math.max(lo, x)) : d;
};

/** Every field present and sane — saved JSON may be partial, old or hand-edited. */
export function normalizeSettings(s) {
  const src = s && typeof s === 'object' ? s : {};
  const d = DEFAULT_SETTINGS;
  const bills = Array.isArray(src.bills) ? src.bills : [];
  return {
    v: 1,
    tariff: normalizeTariff(src.tariff || d.tariff),
    cycleDay: int(src.cycleDay, 1, 28, d.cycleDay),
    offDays: Array.isArray(src.offDays)
      ? [...new Set(src.offDays.map(Number).filter((x) => x >= 0 && x <= 6))].sort()
      : [...d.offDays],
    workStart: int(src.workStart, 0, 23, d.workStart),
    workEnd: int(src.workEnd, 1, 24, d.workEnd),
    sanctionedKw: Math.max(0, Number(src.sanctionedKw) || 0),
    bills: bills
      .filter((b) => b && /^\d{4}-\d{2}$/.test(String(b.month)))
      .map((b) => ({
        month: String(b.month),
        units: Math.max(0, Number(b.units) || 0),
        amount: b.amount === '' || b.amount == null ? null : Number(b.amount) || null,
        estimated: !!b.estimated,
      }))
      .sort((a, b) => (a.month < b.month ? 1 : -1)),
    appliances: (Array.isArray(src.appliances) ? src.appliances : [])
      .filter((a) => a && [1, 2, 3].includes(Number(a.phase)))
      .slice(0, 3)
      .map((a) => ({
        name: String(a.name || 'Appliance').slice(0, 40),
        phase: Number(a.phase),
        minW: Math.min(50000, Math.max(20, Math.round(Number(a.minW) || 500))),
      })),
  };
}

function readLocal(uno) {
  try {
    const s = localStorage.getItem(LOCAL + uno);
    return s ? JSON.parse(s) : null;
  } catch (e) {
    return null;
  }
}

function writeLocal(uno, settings) {
  try {
    localStorage.setItem(LOCAL + uno, JSON.stringify(settings));
  } catch (e) { /* storage unavailable */ }
}

/**
 * -> { settings, stored: 'server' | 'local' | 'default', serverColumn: boolean }
 * Never throws: a failed read gives the defaults (or this browser's copy).
 */
export async function loadEnergySettings(device) {
  const uno = String(device && device.UniqueNumber);
  const local = readLocal(uno);
  try {
    let id = device && device.ID;
    if (id == null) {
      const res = await getDeviceDetailFresh(uno);
      id = (Array.isArray(res.data) ? res.data[0] : null)?.ID;
    }
    if (id != null) {
      const res = await getDeviceInstalledById(id);
      const row = Array.isArray(res.data) ? res.data[0] : null;
      // The row also carries WifiPassword — only this one field is kept.
      if (row && Object.prototype.hasOwnProperty.call(row, 'EnergySettings')) {
        const raw = row.EnergySettings;
        if (raw) {
          try {
            return { settings: normalizeSettings(JSON.parse(raw)), stored: 'server', serverColumn: true };
          } catch (e) { /* unreadable JSON — fall through to the defaults */ }
        }
        // Column exists, nothing saved yet: a local copy from before the
        // migration is the user's work — offer it rather than lose it.
        if (local) return { settings: normalizeSettings(local), stored: 'local', serverColumn: true };
        return { settings: normalizeSettings(null), stored: 'default', serverColumn: true };
      }
    }
  } catch (e) { /* offline or refused — below */ }
  if (local) return { settings: normalizeSettings(local), stored: 'local', serverColumn: false };
  return { settings: normalizeSettings(null), stored: 'default', serverColumn: false };
}

/** -> { ok: true } | { ok: false, local: true, message } */
export async function saveEnergySettings(uniqueNumber, settings) {
  const uno = String(uniqueNumber);
  const clean = normalizeSettings(settings);
  writeLocal(uno, clean);
  const notOnServer = {
    ok: false,
    local: true,
    message: 'Saved on this device only — the server has no EnergySettings column yet '
      + '(energy_settings_migration.sql).',
  };
  try {
    const res = await updateDeviceInstalled(uno, { EnergySettings: JSON.stringify(clean) });
    const d = res && res.data;
    if (d && (d.err || d.status === '500' || d.message === 'some error occured')) return notOnServer;
    return { ok: true };
  } catch (e) {
    if (e && e.response && e.response.status === 500) return notOnServer;
    return { ok: false, local: true, message: 'Could not reach the server — saved on this device only.' };
  }
}
